import { Button, Input, Modal, Select, message } from "antd";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  getComboNames,
  setComboNames,
} from "../../../api/internal_tool_endpoints";
import { foldSearch } from "../../../utils/searchFold";

// The longest name the server accepts, as a column head carries it.
const COMBO_NAME_MAX_LENGTH = 40;

/**
 * The `combo_metric.labels` keys this editor owns, with the label staff see, a
 * hint, and what the page shows when the name is blank. The `key_accounts_*`
 * names reach Key Accounts only; `fixed_combo` and `option_combo` reach every
 * other page that splits fixed and option combos.
 */
const COMBO_NAME_FIELDS = [
  {
    key: "key_accounts_fixed",
    label: "Fixed combo",
    hint: "Combos where the brand's drink is a fixed part of the meal.",
    placeholder: "Default: <brand> Combo",
  },
  {
    key: "key_accounts_bundle",
    label: "Bundle add",
    hint: "Not measured yet. A name here adds a column and tile line that show N/A.",
    placeholder: "Default: not shown",
  },
  {
    key: "key_accounts_option",
    label: "Upsell",
    hint: "Combos that offer the brand's drink as an option.",
    placeholder: "Default: <brand> Upgrade",
  },
  {
    key: "fixed_combo",
    label: "Fixed combo (Execution View and other pages)",
    hint: "Combos with the drink as a fixed part, outside Key Accounts.",
    placeholder: "Default: Fixed Combo",
  },
  {
    key: "option_combo",
    label: "Option combo (Execution View and other pages)",
    hint: "Combos with the drink as an option, outside Key Accounts.",
    placeholder: "Default: Option Combo",
  },
];

const EMPTY_NAMES = Object.fromEntries(
  COMBO_NAME_FIELDS.map(({ key }) => [key, ""]),
);

// A C0 or C1 control character, or DEL: the characters the server refuses.
const isControl = (c) => {
  const code = c.charCodeAt(0);
  return code < 32 || (code >= 127 && code < 160);
};

/** Why one name cannot be saved, or null. Mirrors the server's check. */
function comboNameError(value) {
  const trimmed = (value ?? "").trim();
  if (trimmed.length > COMBO_NAME_MAX_LENGTH) {
    return `Use at most ${COMBO_NAME_MAX_LENGTH} characters.`;
  }
  if ([...trimmed].some(isControl)) {
    return "Remove the control character.";
  }
  return null;
}

/**
 * Set the names a workspace's pages give its combo splits. They live under
 * `workspace_entitlements/{id_client}.combo_metric.labels`; Save writes only
 * these keys, so the combo basis and every other label stay. A blank name
 * removes its key and the page falls back to its default.
 *
 * @param {boolean}  open
 * @param {string[]} workspaces - id_clients to choose from.
 * @param {Function} onCancel
 */
const ManageComboNamesModal = ({ open, workspaces, onCancel }) => {
  const [idClient, setIdClient] = useState(null);
  const [names, setNames] = useState(EMPTY_NAMES);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // Counts workspace reads. Only the newest read may fill the fields.
  const generationRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    generationRef.current += 1;
    setIdClient(null);
    setNames(EMPTY_NAMES);
    setLoading(false);
    setLoadFailed(false);
  }, [open]);

  // Read the picked workspace's stored names. A failed read leaves the fields
  // disabled, so staff cannot save blanks over names they never saw. A read
  // that a later pick or a reopen has superseded changes nothing, so one
  // workspace's names never land in another's fields.
  const selectWorkspace = async (ws) => {
    const gen = ++generationRef.current;
    const isStale = () => generationRef.current !== gen;
    setIdClient(ws);
    setNames(EMPTY_NAMES);
    setLoadFailed(false);
    setLoading(true);
    try {
      const stored = await getComboNames(ws);
      if (isStale()) return;
      setNames(
        Object.fromEntries(
          COMBO_NAME_FIELDS.map(({ key }) => [key, stored?.[key] ?? ""]),
        ),
      );
    } catch (e) {
      if (isStale()) return;
      setLoadFailed(true);
      message.error(e.message || "Failed to load combo names.");
    }
    setLoading(false);
  };

  const errors = Object.fromEntries(
    COMBO_NAME_FIELDS.map(({ key }) => [key, comboNameError(names[key])]),
  );
  const invalid = Object.values(errors).some(Boolean);

  const handleSave = async () => {
    setSaving(true);
    try {
      const trimmed = Object.fromEntries(
        COMBO_NAME_FIELDS.map(({ key }) => [key, names[key].trim()]),
      );
      await setComboNames({ id_client: idClient, ...trimmed });
      message.success(`Combo names updated for ${idClient}.`);
      onCancel();
    } catch (e) {
      message.error(e.message || "Failed to update combo names.");
    } finally {
      setSaving(false);
    }
  };

  const options = useMemo(
    () => (workspaces || []).map((w) => ({ value: w, label: w })),
    [workspaces],
  );

  return (
    <Modal
      title="Combo names"
      open={open}
      onCancel={onCancel}
      width={560}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!idClient || loading || loadFailed || invalid}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]}
    >
      <div className="mb-3">
        <div className="text-[0.8rem] text-zinc-500 mb-1">Workspace</div>
        <Select
          showSearch
          placeholder="Select a workspace"
          value={idClient}
          onChange={selectWorkspace}
          options={options}
          style={{ width: "100%" }}
          aria-label="Workspace"
          filterOption={(q, opt) =>
            foldSearch(opt?.label).includes(foldSearch(q))
          }
        />
      </div>

      {idClient ? (
        <>
          <p className="text-[0.8rem] text-zinc-500 mb-3">
            Names for the combo splits on this workspace&apos;s pages and their
            exports. Leave a name empty to use the default.
          </p>
          {COMBO_NAME_FIELDS.map(({ key, label, hint, placeholder }) => (
            <div key={key} className="mb-3">
              <label
                htmlFor={`combo-name-${key}`}
                className="block text-[0.8rem] font-medium text-zinc-700 mb-1"
              >
                {label}
              </label>
              <Input
                id={`combo-name-${key}`}
                value={names[key]}
                placeholder={placeholder}
                disabled={loading || loadFailed}
                status={errors[key] ? "error" : undefined}
                onChange={(e) =>
                  setNames((n) => ({ ...n, [key]: e.target.value }))
                }
              />
              <div
                className={`text-[0.75rem] mt-1 ${
                  errors[key] ? "text-red-600" : "text-zinc-400"
                }`}
              >
                {errors[key] || hint}
              </div>
            </div>
          ))}
        </>
      ) : (
        <p className="text-[0.8rem] text-zinc-400 italic">
          Select a workspace to view and edit its combo names.
        </p>
      )}
    </Modal>
  );
};

ManageComboNamesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  workspaces: PropTypes.arrayOf(PropTypes.string),
  onCancel: PropTypes.func.isRequired,
};

export default ManageComboNamesModal;
